import type { DesignData } from "../neon/types";

/**
 * Pack a design into a URL hash. We avoid third-party compression so the
 * bundle stays small; for typical sign designs (a few tubes, ~30 points
 * each) the resulting URL comfortably fits under 8 KB.
 */
export function encodeDesign(data: DesignData): string {
  const json = JSON.stringify(data);
  const bytes = new TextEncoder().encode(json);
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return base64UrlEncode(bin);
}

export function decodeDesign(encoded: string): DesignData | null {
  try {
    const bin = base64UrlDecode(encoded);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    const json = new TextDecoder().decode(bytes);
    const parsed = JSON.parse(json);
    if (parsed?.version !== 1 || !Array.isArray(parsed.tubes)) return null;
    return parsed as DesignData;
  } catch {
    return null;
  }
}

export function writeToHash(data: DesignData): string {
  const encoded = encodeDesign(data);
  const slug = slugify(data.name);
  const hash = `#d=${slug}/${encoded}`;
  if (typeof history !== "undefined" && history.replaceState) {
    history.replaceState(null, "", `${location.pathname}${location.search}${hash}`);
  } else {
    location.hash = hash;
  }
  return `${location.origin}${location.pathname}${hash}`;
}

export function readFromHash(): DesignData | null {
  const h = location.hash;
  if (!h.startsWith("#d=")) return null;
  const body = h.slice(3);
  const slashIdx = body.indexOf("/");
  const encoded = slashIdx >= 0 ? body.slice(slashIdx + 1) : body;
  return decodeDesign(encoded);
}

function slugify(name: string): string {
  return (
    name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 40) || "sign"
  );
}

function base64UrlEncode(bin: string): string {
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function base64UrlDecode(s: string): string {
  const pad = s.length % 4 === 0 ? "" : "=".repeat(4 - (s.length % 4));
  return atob(s.replace(/-/g, "+").replace(/_/g, "/") + pad);
}
